import { focusManager } from '@tanstack/react-query';
import { useEffect } from 'react';

/**
 * As explained in https://github.com/TanStack/query/pull/4805, the `refetchOnWindowFocus` query option
 * no longer triggers a refetch if the user returns from another application window but only if they
 * switch between tabs within the very same browser window. According to the link above, the reason for this change is to prevent
 * unintended refetching when interacting with iframes, browser dialogs, and the developer console. Since none of these
 * reasons apply to our app in production usage scenarios, this restores the old behavior that refetches data also if the user
 * returns from another application window. This improves the consistency of data displayed in open app sessions.
 */
export function useTriggerWindowFocus() {
  useEffect(() => {
    focusManager.setEventListener((handleFocus: (focused: boolean) => void) => {
      const handler = (event: Event): void => {
        switch (event.type) {
          case 'focus':
            handleFocus(true);
            break;
          case 'blur':
            handleFocus(false);
            break;
          case 'visibilitychange':
            handleFocus(document.visibilityState === 'visible');
            break;
        }
      };

      window.addEventListener('visibilitychange', handler, false);
      window.addEventListener('focus', handler, false);
      window.addEventListener('blur', handler, false);

      return (): void => {
        window.removeEventListener('visibilitychange', handler);
        window.removeEventListener('focus', handler);
        window.removeEventListener('blur', handler);
      };
    });
  }, []);
}
